import React, { useContext, Suspense } from 'react';
import './selected-item-details.scss';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { Rating } from '@thd-olt-component-react/core-ui';
import { Price } from '@thd-olt-component-react/price';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { Favorite } from '@thd-olt-component-react/save-to-favorites';
import { bool, string, shape } from 'prop-types';
import { Quantity } from '@thd-olt-component-react/quantity';
import { ProductDetailsButton } from './ProductDetailsButton';
import { FulfillmentLazy } from './FulfillmentLazy';
import { QuantityAdjustmentsContext } from '../QuantityAdjustmentsContext';

const SelectedItemDetails = (props) => {
  const {
    itemId,
    hoveredItemId,
    storeId,
    channel,
    displayMobile,
    dataSource = 'catalog',
    data,
    packageCategory,
    mountFulfillment
  } = props;

  const { itemMap, setItemMap } = useContext(QuantityAdjustmentsContext);

  const {
    reviews = {},
    identifiers = {},
    keyProductFeatures = {}
  } = data || {};

  const { averageRating, totalReviews } = reviews.ratingsReviews || {};
  const { modelNumber, canonicalUrl, brandName, productLabel } = identifiers;
  const isGM = packageCategory === 'gm';
  const hasKeyProductFeatures = !!keyProductFeatures?.keyProductFeaturesItems?.[0]?.features?.length;

  const mountedFn = () => {
    return storeId !== '8119';
  };

  if (storeId === '8119' && !data.itemId) return null;

  const handleQuantityChange = ({ quantity }) => {
    const map = { ...itemMap, [itemId]: { ...itemMap[itemId], selectedQty: quantity } };
    setItemMap(map);
  };

  return (
    <QueryProvider
      mounted={mountedFn}
      dataSource={dataSource}
      defaultVariables={{ itemId, storeId }}
      cacheKey="selected-item-details"
    >
      <div className="bundling-product-tabs__info" data-testid="selected-item-details">
        <div className="bundling-product-tabs__reviews-and-favorites">
          <a href={canonicalUrl} className="bundling-product-tabs__reviews">
            <div className="bundling-product-tabs__reviews-container">
              <Rating value={parseFloat(averageRating)} type="stars" orange />
              <span className="bundling-product-tabs__reviews-container-total">({totalReviews})</span>
            </div>
            <span className="bundling-product-tabs__review-divider">|</span>
            <div className="bundling-product-tabs__reviews-container-model">Model # {modelNumber}</div>
          </a>
          <div className="bundling-product-tabs__favorites">
            <Favorite showCircle itemId={itemId} storeId={storeId} />
          </div>
        </div>
        <div className="bundling-product-tabs__branding">
          <a href={canonicalUrl}>
            <span className="bundling-product-tabs__branding-brand">{brandName}</span>
            <span className="bundling-product-tabs__branding-product">{productLabel}</span>
          </a>
        </div>
        <div className="bundling-product-tabs__pricing">
          <Price itemId={itemId} channel={channel} storeId={storeId} displayEachUom={false} omitDetails large={false} />
        </div>
        {hasKeyProductFeatures && (
          <div className="bundling-product-tabs__specs">
            <p className="bundling-product-tabs__specs-title">Top Specs</p>
            <KeyProductFeatures
              itemId={itemId}
              hideImage
              maxfeatures={6}
              twoColumnGrid={!displayMobile}
              displaySimilarLinks={false}
              simple
            />
          </div>
        )}
        {!hasKeyProductFeatures && isGM && (
          <div className="bundling-product-tabs__salient-points" data-testid="salient-points">
            <QueryProvider cacheKey="selected-item-details-salient-points">
              <SalientPoints itemId={itemId} max={3} />
            </QueryProvider>
          </div>
        )}
        <div className="fulfillment-layout">
          {isGM && mountFulfillment ? (
            <Suspense fallback={<div />}>
              <QueryProvider
                dataSource="catalog"
                cacheKey="selected-item-details-fulfillment"
              >
                <FulfillmentLazy
                  itemId={itemId}
                  hoveredItemId={hoveredItemId}
                />
              </QueryProvider>
            </Suspense>
          ) : <span />}

          <div className="fulfillment-layout__row">
            <ProductDetailsButton itemId={itemId} />

            {isGM && (
              <div className="bundling-product-tabs__quantity" role="button">
                <Quantity value={itemMap[itemId]?.selectedQty || 1} onChange={handleQuantityChange} noRightMargin />
              </div>
            )}
          </div>
        </div>
      </div>
    </QueryProvider>
  );
};

SelectedItemDetails.displayName = 'SelectedItemDetails';

SelectedItemDetails.propTypes = {
  itemId: string,
  hoveredItemId: string,
  storeId: string,
  channel: string,
  displayMobile: bool,
  dataSource: string,
  data: shape({}),
  packageCategory: string,
  mountFulfillment: bool
};

SelectedItemDetails.defaultProps = {
  itemId: '',
  hoveredItemId: '',
  storeId: '',
  channel: '',
  displayMobile: false,
  dataSource: 'catalog',
  data: {},
  packageCategory: '',
  mountFulfillment: false
};

export { SelectedItemDetails };
